// Hooks de datos del Semáforo sobre el cliente RPC tipado. Los tipos se
// infieren de las rutas del Worker: si cambia la API, cambia aquí.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InferResponseType } from "hono/client";
import { leer, type Api } from "../api";
import { useSesion } from "../sesion";

type Rutas = Api["api"]["tickets"];
export type Yo = InferResponseType<Rutas["yo"]["$get"], 200>;
export type Catalogo = InferResponseType<Rutas["catalogo"]["$get"], 200>;
export type Bandeja = InferResponseType<Rutas["$get"], 200>;
export type Ticket = Bandeja["tickets"][number];
export type Interpretacion = InferResponseType<Rutas["interpretar"]["$post"], 200>;
export type Detalle = InferResponseType<Rutas[":id"]["$get"], 200>;
export type Avisos = InferResponseType<Rutas["avisos"]["$get"], 200>;
export type Tablero = InferResponseType<Api["api"]["tablero"]["$get"], 200>;
export type VistaArea = InferResponseType<Api["api"]["tablero"]["area"][":clave"]["$get"], 200>;
export type Periodo = "hoy" | "semana" | "mes";

export const COOKIE_PERSONA_LOCAL = "iavmx_persona_local";

export function usePersona() {
  const { api, cargando } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "yo"],
    queryFn: () => api.api.tickets.yo.$get().then((r) => leer<Yo>(r)),
    enabled: !cargando,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useCatalogo() {
  const { api } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "catalogo"],
    queryFn: () => api.api.tickets.catalogo.$get().then((r) => leer<Catalogo>(r)),
    staleTime: 5 * 60_000,
  });
}

export function useBandeja(q: {
  alcance?: "abiertos" | "todos" | "cerrados";
  estado?: string;
  area?: string;
  semaforo?: "verde" | "ambar" | "rojo" | "vencido";
  mios?: "1" | "0";
  q?: string;
}) {
  const { api } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "bandeja", q],
    queryFn: () => api.api.tickets.$get({ query: q as never }).then((r) => leer<Bandeja>(r)),
  });
}

export function useDetalle(id: string) {
  const { api } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "ticket", id],
    queryFn: () => api.api.tickets[":id"].$get({ param: { id } }).then((r) => leer<Detalle>(r)),
  });
}

export function useTablero(periodo: Periodo) {
  const { api } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "tablero", periodo],
    queryFn: () => api.api.tablero.$get({ query: { periodo } }).then((r) => leer<Tablero>(r)),
  });
}

export function useVistaArea(clave: string | null, periodo: Periodo) {
  const { api } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "area", clave, periodo],
    queryFn: () =>
      api.api.tablero.area[":clave"]
        .$get({ param: { clave: clave! }, query: { periodo } })
        .then((r) => leer<VistaArea>(r)),
    enabled: Boolean(clave),
  });
}

export function useAvisos() {
  const { api } = useSesion();
  return useQuery({
    queryKey: ["semaforo", "avisos"],
    queryFn: () => api.api.tickets.avisos.$get().then((r) => leer<Avisos>(r)),
  });
}

/** Invalida todo lo del Semáforo después de una mutación. */
export function useRefrescar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["semaforo"] });
}

export function useAccionTicket(id: string) {
  const { api } = useSesion();
  const refrescar = useRefrescar();
  const t = api.api.tickets[":id"];
  const param = { id };
  const asignar = useMutation({
    mutationFn: (json: { personaId: string; areaId?: string; nota?: string }) =>
      t.asignar.$post({ param, json }).then((r) => leer(r)),
    onSuccess: refrescar,
  });
  const mover = useMutation({
    mutationFn: (json: { estado: string; comentario?: string }) =>
      t.estado.$post({ param, json: json as never }).then((r) => leer(r)),
    onSuccess: refrescar,
  });
  const prioridad = useMutation({
    mutationFn: (json: { prioridad: "baja" | "normal" | "alta" | "urgente" }) =>
      t.prioridad.$post({ param, json }).then((r) => leer(r)),
    onSuccess: refrescar,
  });
  const comentar = useMutation({
    mutationFn: (json: { texto: string }) => t.comentarios.$post({ param, json }).then((r) => leer(r)),
    onSuccess: refrescar,
  });
  const simularVencimiento = useMutation({
    mutationFn: () => t["simular-vencimiento"].$post({ param }).then((r) => leer(r)),
    onSuccess: refrescar,
  });
  return { asignar, mover, prioridad, comentar, simularVencimiento };
}

export function useCronLocal() {
  const { api } = useSesion();
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: () => api.api.tickets.cron.$post().then((r) => leer<{ recalculados: number; escalados: number; cerrados: number }>(r)),
    onSuccess: refrescar,
  });
}

/** Cambia la persona local (solo desarrollo): cookie y recarga de datos. */
export function fijarPersonaLocal(correo: string | null) {
  document.cookie = correo
    ? `${COOKIE_PERSONA_LOCAL}=${encodeURIComponent(correo)}; path=/; SameSite=Lax`
    : `${COOKIE_PERSONA_LOCAL}=; path=/; Max-Age=0`;
}
